import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FlagBanner, CheckCircle, Clock, EnvelopeSimple, Trash, Copy,
  GoogleLogo, AppleLogo, ArrowClockwise,
} from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-ochre-400/15 text-ochre-600 border-ochre-400/40", Icon: Clock },
  invited: { label: "Invited", color: "bg-sage-400/15 text-sage-600 border-sage-400/40", Icon: EnvelopeSimple },
  installed: { label: "Installed", color: "bg-terracotta-50 text-terracotta-600 border-terracotta-500", Icon: CheckCircle },
  removed: { label: "Removed", color: "bg-sand-200 text-stoneink-500 border-sand-300", Icon: Trash },
};

export default function BetaAdmin() {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/beta/signups");
      setSignups(data || []);
    } catch (err) {
      toast.error(formatError(err) || "Could not load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/beta/signups/${id}`, { status });
      setSignups((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this signup record? This does not remove them from the Play Console.")) return;
    try {
      await api.delete(`/beta/signups/${id}`);
      setSignups((rows) => rows.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const copyEmails = () => {
    const text = filtered.map((s) => s.email).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${filtered.length} email${filtered.length === 1 ? "" : "s"} to clipboard`);
  };

  const filtered = filter === "all" ? signups : signups.filter((s) => s.status === filter);
  const counts = signups.reduce((acc, s) => {
    acc[s.status || "pending"] = (acc[s.status || "pending"] || 0) + 1;
    return acc;
  }, {});

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="beta-admin-page">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-terracotta-600 mb-1">
              <FlagBanner weight="duotone" size={14} /> Admin · Beta testers
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-stoneink-900 font-semibold tracking-tight">
              Closed-beta signups
            </h1>
            <p className="mt-2 text-stoneink-700">
              Use this list to add testers to the Google Play closed-test track.
              Mark <em>Invited</em> once you've added them in the Play Console.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="beta-admin-refresh"
              className="h-10 px-4 rounded-xl bg-white border border-sand-300 text-stoneink-700 hover:bg-sand-100 text-sm font-medium flex items-center gap-2">
              <ArrowClockwise size={14} /> Refresh
            </button>
            <button onClick={copyEmails} disabled={filtered.length === 0}
              data-testid="beta-admin-copy-emails"
              className="h-10 px-4 rounded-xl bg-stoneink-900 hover:bg-stoneink-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-40">
              <Copy size={14} /> Copy emails
            </button>
          </div>
        </header>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2">
          <FilterPill label={`All (${signups.length})`} active={filter === "all"} onClick={() => setFilter("all")} />
          {["pending", "invited", "installed", "removed"].map((s) => (
            <FilterPill key={s}
              label={`${STATUS_META[s].label} (${counts[s] || 0})`}
              active={filter === s}
              onClick={() => setFilter(s)} />
          ))}
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-white border border-sand-300 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-stoneink-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-stoneink-700">No signups yet in this view.</p>
              <p className="mt-2 text-sm text-stoneink-500">
                Share <Link to="/beta" className="text-terracotta-600 hover:underline">nestledapp.io/beta</Link> to start collecting testers.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sand-100 text-stoneink-700">
                  <tr>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-left p-3 font-medium">Platform</th>
                    <th className="text-left p-3 font-medium">Signed up</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <TesterRow key={s.id} row={s}
                      onMark={(status) => updateStatus(s.id, status)}
                      onDelete={() => remove(s.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

const FilterPill = ({ label, active, onClick }) => (
  <button onClick={onClick}
    className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${
      active ? "bg-terracotta-500 text-white" : "bg-white border border-sand-300 text-stoneink-700 hover:bg-sand-100"
    }`}>
    {label}
  </button>
);

const TesterRow = ({ row, onMark, onDelete }) => {
  const status = row.status || "pending";
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  const PlatformIcon = row.platform === "ios" ? AppleLogo : GoogleLogo;
  return (
    <tr className="border-t border-sand-300 hover:bg-sand-100/50" data-testid={`beta-row-${row.id}`}>
      <td className="p-3 font-medium text-stoneink-900">{row.name || "—"}</td>
      <td className="p-3 text-stoneink-700">
        <button onClick={() => { navigator.clipboard.writeText(row.email); toast.success("Email copied"); }}
          className="hover:text-terracotta-600 inline-flex items-center gap-1.5">
          {row.email}
          <Copy size={12} className="opacity-50" />
        </button>
      </td>
      <td className="p-3">
        <span className="inline-flex items-center gap-1.5 text-stoneink-700">
          <PlatformIcon weight="duotone" size={14} /> {row.platform === "ios" ? "iOS" : "Android"}
        </span>
      </td>
      <td className="p-3 text-stoneink-500 text-xs">
        {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
      </td>
      <td className="p-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${meta.color}`}>
          <Icon weight="duotone" size={12} /> {meta.label}
        </span>
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        {status === "pending" && (
          <button onClick={() => onMark("invited")} data-testid={`beta-mark-invited-${row.id}`}
            className="h-8 px-3 rounded-lg bg-sage-400/10 border border-sage-400/40 text-sage-600 hover:bg-sage-400/20 text-xs font-medium mr-1.5">
            Mark invited
          </button>
        )}
        {status === "invited" && (
          <button onClick={() => onMark("installed")} data-testid={`beta-mark-installed-${row.id}`}
            className="h-8 px-3 rounded-lg bg-terracotta-50 border border-terracotta-500 text-terracotta-600 hover:bg-terracotta-100 text-xs font-medium mr-1.5">
            Mark installed
          </button>
        )}
        <button onClick={onDelete} data-testid={`beta-row-delete-${row.id}`}
          className="h-8 w-8 rounded-lg text-stoneink-500 hover:bg-destructive/10 hover:text-destructive inline-flex items-center justify-center">
          <Trash size={14} />
        </button>
      </td>
    </tr>
  );
};
